import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaInfoCircle } from 'react-icons/fa';
import logo from '../images/logo.svg';
import axios from 'axios';

function StageOne({ formData, onSubmit, inviteCode, isUpdate = false }) {
  const [localFormData, setLocalFormData] = useState(formData);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  // Get team info from localStorage
  const selectedTeamType = sessionStorage.getItem('selected_team_type');
  const selectedTeamAgeGroup = sessionStorage.getItem('selected_team_age_group');
  
  // Determine if this is an academy team with U13s or younger
  const isAcademyU13OrYounger = () => {
    if (selectedTeamType !== 'academy') return false;
    if (!selectedTeamAgeGroup) return false;
    
    // Extract age from age group (e.g., "U13s" -> 13, "U12s" -> 12)
    const match = selectedTeamAgeGroup.match(/U(\d+)s?/i);
    if (!match) return false;
    
    const age = parseInt(match[1]);
    return age <= 13;
  };

  useEffect(() => {
    const fetchAdminDetails = async () => {
      // Try to get code from props first (from manual input), then from URL
      const urlParams = new URLSearchParams(window.location.search);
      const codeFromUrl = urlParams.get('code');
      
      // Use invite code from props if available, otherwise use from URL
      const code = inviteCode || codeFromUrl;
      
      if (!code) return;
      
      try {
        setIsLoading(true);
        
        // Parse the team ID from the code (everything before the hyphen)
        const teamId = code.split('-')[0];
        if (!teamId) return;
        
        // Make API call to get admin details
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/register/get_team_admin.php?team_id=${teamId}`);
        
        if (response.data.success && response.data.admin) {
          const { first_name, last_name, email, phone } = response.data.admin;
          
          // Pre-fill form data with admin details
          setLocalFormData(prevData => ({
            ...prevData,
            firstName: first_name ? first_name.charAt(0).toUpperCase() + first_name.slice(1) : '',
            lastName: last_name ? last_name.charAt(0).toUpperCase() + last_name.slice(1) : '',
            email: email || '',
            phone: phone || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching admin details:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAdminDetails();
  }, [inviteCode]);

  useEffect(() => {
    // Update local form data when props change
    setLocalFormData(formData);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalFormData({ ...localFormData, [name]: value });
  };

  const validate = () => {
    const newErrors = {};
    if (!localFormData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!localFormData.lastName.trim()) newErrors.lastName = 'Last name is required';
    
    // Only validate phone and email if not an academy U13 or younger team
    const shouldShowContactFields = !isAcademyU13OrYounger();
    
    if (shouldShowContactFields) {
      // Phone validation
      const phoneValue = localFormData.phone.trim();
      if (!phoneValue) {
        newErrors.phone = 'Phone number is required';
      } else {
        // Validate that phone starts with + or 0
        if (!phoneValue.startsWith('+') && !phoneValue.startsWith('0')) {
          newErrors.phone = 'Phone number must start with + country code or 0';
        }
        // Basic format validation
        const phoneRegex = /^(\+\d{1,3}|0)\d{10,15}$/;
        if (!phoneRegex.test(phoneValue)) {
          newErrors.phone = 'Please enter a valid phone number format';
        }
      }
      
      if (!/\S+@\S+\.\S+/.test(localFormData.email)) newErrors.email = 'Email is invalid';
    }
    
    if (!isUpdate && localFormData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (isUpdate && localFormData.password.length > 0 && localFormData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    return newErrors;
  };

  const checkPhoneExists = async () => {
    // Skip phone check if this is an academy U13 or younger team or if it's an update
    if (isAcademyU13OrYounger() || isUpdate) {
      return false;
    }
    
    try {
      setIsCheckingPhone(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register/check_player.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          phone: localFormData.phone,
          invite_code: inviteCode 
        })
      });

      const data = await response.json();
      setIsCheckingPhone(false);
      
      if (data.exists) {
        // Phone already exists, show error
        setErrors({
          ...errors,
          phone: 'This phone number is already registered. Please login instead.'
        });
        return true; // Phone exists
      }
      
      return false; // Phone doesn't exist
    } catch (error) {
      console.error('Error checking phone:', error);
      setIsCheckingPhone(false);
      setErrors({
        ...errors,
        phone: 'Error checking phone number. Please try again.'
      });
      return true; // Return true to prevent form submission on error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // First validate the form
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Then check if phone number already exists
    const phoneExists = await checkPhoneExists();
    if (phoneExists) {
      return;
    }
    
    // All clear, proceed to next stage
    setErrors({});
    onSubmit(localFormData);
  };

  return (
    <div className="register-stage">
      <div className="stage-indicator">
        <div className="stage active">1</div>
        <div className="stage-line"></div>
        <div className="stage">2</div>
      </div>
      
      <h3 className="stage-title">{isUpdate ? 'Update Profile Information' : 'Basic Information'}</h3>
      
      {isLoading ? (
        <div className="loading-indicator">Loading user details...</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
  
            <label>First Name</label>
              <input 
              type="text" 
              name="firstName" 
              value={localFormData.firstName} 
              onChange={handleChange} 
              required 
              />
              {errors.firstName && <span className="error">{errors.firstName}</span>}
  
              <label>Last Name</label>
                <input 
                  type="text" 
                  name="lastName" 
                  value={localFormData.lastName} 
                  onChange={handleChange} 
                  required 
                />
                {errors.lastName && <span className="error">{errors.lastName}</span>}
  
            {/* Only show contact fields if not an academy U13 or younger team */}
            {!isAcademyU13OrYounger() ? (
              <>
                <label>Mobile</label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={localFormData.phone} 
                  onChange={handleChange} 
                  required 
                  placeholder="e.g. +441234567890 or 07123456789"
                  maxLength="20"
                />
                {errors.phone && <span className="error">{errors.phone}</span>}
                
                <label>Email</label>
                <input 
                  type="email" 
                  name="email" 
                  value={localFormData.email} 
                  onChange={handleChange} 
                  required 
                />
                {errors.email && <span className="error">{errors.email}</span>}
              </>
            ) : (
              <div className="info-message">
                <p>
                  <FaInfoCircle className="info-icon" />
                  Contact details are not required for academy players aged 13 or younger. These can be added later by a parent or guardian.
                </p>
              </div>
            )}
            
            <label>Password{isUpdate ? ' (Optional)' : ''}</label>
            <input 
              type="password" 
              name="password" 
              value={localFormData.password} 
              onChange={handleChange} 
              required={!isUpdate}
              placeholder={isUpdate ? 'Leave blank to keep current password' : ''}
            />
            {errors.password && <span className="error">{errors.password}</span>}
          </div>
          
          <button type="submit" className="next-button" disabled={isCheckingPhone}>
            {isCheckingPhone ? 'Checking...' : (isUpdate ? 'Continue' : 'Next')} {!isCheckingPhone && <span className="chevron-right">→</span>}
          </button>
          
          <div className="form-links">
            <Link to="/">Back to Login</Link>
          </div>
        </form>
      )}
    </div>
  );
}

export default StageOne; 